import type { LifecycleListener } from "..";
import { instrumentSdkMethod } from "../instrumentation";
import type { ModuleContext } from "./module-context";

type LifecycleEvent = "hide" | "show" | "exitRequested";

export class LifecycleModule {
  private listeners: Record<LifecycleEvent, Map<symbol, LifecycleListener>> = {
    hide: new Map(),
    show: new Map(),
    exitRequested: new Map(),
  };
  private hidden: boolean | null = null;

  constructor(private ctx: ModuleContext) {
    this.ctx.registerMessageHandlers({
      PlatformExitRequested: () => this.emit("exitRequested"),
    });

    if (typeof document === "undefined") {
      return;
    }

    this.hidden = document.hidden;
    document.addEventListener("visibilitychange", () => {
      const hidden = document.hidden;
      if (hidden === this.hidden) {
        return;
      }

      this.hidden = hidden;
      this.emit(hidden ? "hide" : "show");
    });
  }

  @instrumentSdkMethod({ name: "lifecycle.onHide" })
  onHide(listener: LifecycleListener): () => void {
    return this.subscribe("hide", listener);
  }

  @instrumentSdkMethod({ name: "lifecycle.onShow" })
  onShow(listener: LifecycleListener): () => void {
    return this.subscribe("show", listener);
  }

  @instrumentSdkMethod({ name: "lifecycle.onExitRequested" })
  onExitRequested(listener: LifecycleListener): () => void {
    return this.subscribe("exitRequested", listener);
  }

  instrumentMethod<T>(opts: { method: string; args: unknown; fn: () => T }): T {
    return this.ctx.instrumentMethod(opts);
  }

  subscribe(event: LifecycleEvent, listener: LifecycleListener): () => void {
    if (typeof listener !== "function") {
      throw new Error("INVALID_ARGUMENTS: listener must be a function");
    }

    const id = Symbol();
    this.listeners[event].set(id, listener);
    return () => {
      this.listeners[event].delete(id);
    };
  }

  private emit(event: LifecycleEvent): void {
    const report = (error: unknown) => {
      console.error(`Error in ${event} lifecycle listener`, error);
    };

    for (const listener of Array.from(this.listeners[event].values())) {
      try {
        void Promise.resolve(listener()).catch(report);
      } catch (error) {
        report(error);
      }
    }
  }
}
